import React, { useState, useEffect } from 'react';
import Button from '../ui/Button';
import Icon from '../AppIcon';
import api from 'utils/api';
import dayjs from 'dayjs';
import jalaliday from 'jalaliday';
import SendPaymentModal from './SendPaymentModal';
import { toast } from 'react-hot-toast';

dayjs.extend(jalaliday);

const ClientHistoryModal = ({ isOpen, onClose, client }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  // برای مودال پرداخت
  const [selectedBookingForPayment, setSelectedBookingForPayment] =
    useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    if (isOpen && client) {
      fetchHistory();
    }
  }, [isOpen, client]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      // دریافت لیست رزروهای این مشتری
      const res = await api.get(`/providers/ALL/bookings/client/${client._id}`);
      // نکته: چون روت‌ها Nest شده هستند، آدرس دقیق را با بک‌اند چک کنید.
      // اگر روت را در index.js جدا تعریف کردید، آدرس ساده‌تر می‌شود.
      // فرض ما بر اساس کد قبلی: /providers/:providerId/bookings... بود
      // اما بهتر است روت global داشته باشیم.
      // اگر روت بالا در index.js زیر مجموعه /bookings تعریف شده:
      // const res = await api.get(`/bookings/client/${client._id}`);
      // فعلا فرض میکنیم روت در routes/Booking.js و زیر مجموعه /providers/:id/bookings است
      // برای سادگی پیشنهاد میکنم روت جدید را در index.js جداگانه ایمپورت کنید (پایین توضیح میدهم)

      // بیایید فرض کنیم روت را درست کردیم:
      setBookings(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      await api.patch(`/bookings/${bookingId}/status`, { status: newStatus });
      toast.success('وضعیت تغییر کرد');
      fetchHistory(); // رفرش
    } catch (error) {
      toast.error('خطا در تغییر وضعیت');
    }
  };

  const openPayment = (booking) => {
    setSelectedBookingForPayment(booking);
    setShowPaymentModal(true);
  };

  const getStatusBadge = (status) => {
    const map = {
      pending: {
        text: 'در انتظار',
        bg: 'bg-yellow-100',
        color: 'text-yellow-700',
      },
      confirmed: {
        text: 'تایید شده',
        bg: 'bg-green-100',
        color: 'text-green-700',
      },
      cancelled: { text: 'لغو شده', bg: 'bg-red-100', color: 'text-red-700' },
      pending_payment: {
        text: 'منتظر پرداخت',
        bg: 'bg-blue-100',
        color: 'text-blue-700',
      },
      paid: {
        text: 'پرداخت شده',
        bg: 'bg-emerald-100',
        color: 'text-emerald-700',
      },
    };
    const s = map[status] || {
      text: status,
      bg: 'bg-gray-100',
      color: 'text-gray-600',
    };
    return (
      <span className={`px-2 py-1 rounded text-xs ${s.bg} ${s.color}`}>
        {s.text}
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-modal flex items-center justify-center p-4">
      <div className="bg-card w-full max-w-4xl rounded-lg shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-4 border-b border-border flex justify-between items-center bg-muted/30">
          <div>
            <h2 className="text-lg font-bold">تاریخچه خدمات</h2>
            <p className="text-sm text-muted-foreground">
              مشتری: {client?.name || 'بدون نام'} | موبایل:{' '}
              {client?.phoneNumber}
            </p>
          </div>
          <Button variant="ghost" size="icon" iconName="X" onClick={onClose} />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-10">در حال دریافت اطلاعات...</div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              هیچ سابقه سفارشی یافت نشد.
            </div>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking) => (
                <div
                  key={booking._id}
                  className="border border-border rounded-lg p-4 bg-background hover:bg-muted/10 transition">
                  <div className="flex flex-col md:flex-row justify-between gap-4">
                    {/* Info */}
                    <div className="space-y-1 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-primary">
                          {booking.service?.name}
                        </span>
                        {getStatusBadge(booking.status)}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        تاریخ:{' '}
                        {dayjs(booking.start)
                          .calendar('jalali')
                          .format('DDDD D MMMM YYYY - HH:mm')}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        پرووایدر: {booking.provider?.name}
                      </div>
                      <div className="font-medium mt-1">
                        مبلغ: {booking.service?.price?.toLocaleString()} تومان
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        variant="default"
                        iconName="CreditCard"
                        onClick={() => openPayment(booking)}>
                        ارسال لینک پرداخت
                      </Button>

                      {booking.status !== 'confirmed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600 hover:bg-green-50 border-green-200"
                          onClick={() =>
                            handleStatusChange(booking._id, 'confirmed')
                          }>
                          تایید نوبت
                        </Button>
                      )}

                      {booking.status !== 'cancelled' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50 border-red-200"
                          onClick={() =>
                            handleStatusChange(booking._id, 'cancelled')
                          }>
                          لغو
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal inside History */}
      <SendPaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        booking={selectedBookingForPayment}
        onSuccess={() => {
          fetchHistory(); // Refresh list after sending link
        }}
      />
    </div>
  );
};

export default ClientHistoryModal;
